import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class IBCompleteRoutine extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ibRoutineBound === 'true') return;
    this.dataset.ibRoutineBound = 'true';

    this.button = this.querySelector('.ib-complete-routine__add-all');
    this.buttonComponent = this.querySelector(
      '.ib-complete-routine__native-button add-to-cart-component'
    );
    this.liveRegion = this.querySelector('[data-ib-routine-live]');
    this.track = this.querySelector('[data-ib-routine-track]');
    this.priceDataElement = this.querySelector('[data-ib-routine-price-data]');

    this.isAdding = false;
    this.successTimer = null;

    this.setupTrackDrag();
    this.setupAccordion();
    this.setupVariantSync();
    this.updateRoutineTotal();

    if (!this.button) return;

    this.button.addEventListener('click', this.handleAddAll);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleAddAll);
    this.cleanupTrackDrag?.();
    this.cleanupAccordion?.();
    this.cleanupVariantSync?.();

    if (this.successTimer) {
      window.clearTimeout(this.successTimer);
    }
  }

  /* ========================================================
     ROUTINE ACCORDION — ONE PANEL OPEN AT A TIME
     ======================================================== */
  setupAccordion() {
    const accordion = this.querySelector('[data-ib-routine-accordion]');
    if (!accordion || accordion.dataset.ibAccordionBound === 'true') return;

    accordion.dataset.ibAccordionBound = 'true';

    const items = Array.from(
      accordion.querySelectorAll('[data-ib-routine-accordion-item]')
    );

    const onToggle = (event) => {
      const current = event.target;

      if (!(current instanceof HTMLDetailsElement) || !current.open) {
        return;
      }

      items.forEach((item) => {
        if (item !== current) {
          item.open = false;
        }
      });
    };

    items.forEach((item) => {
      item.addEventListener('toggle', onToggle);
    });

    this.cleanupAccordion = () => {
      items.forEach((item) => {
        item.removeEventListener('toggle', onToggle);
      });
    };
  }

  /* ========================================================
     MOBILE / MOUSE DRAG
     ======================================================== */
  setupTrackDrag() {
    if (!this.track || this.track.dataset.ibDragBound === 'true') return;

    this.track.dataset.ibDragBound = 'true';

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let dragging = false;
    let suppressClick = false;

    const threshold = 7;

    const reset = () => {
      pointerId = null;
      dragging = false;
      this.track.classList.remove('is-dragging');
    };

    const snapToNearest = () => {
      const cards = Array.from(
        this.track.querySelectorAll('[data-ib-routine-item]')
      );

      if (!cards.length) return;

      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let nearest = cards[0];
      let nearestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(center - cardCenter);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = card;
        }
      });

      this.track.scrollTo({
        left: nearest.offsetLeft,
        behavior: 'smooth',
      });
    };

    const onPointerDown = (event) => {
      /* Su touch lasciamo lavorare lo scroll nativo del browser.
         Il drag manuale serve soltanto al desktop/mouse. */
      if (event.pointerType !== 'mouse') return;
      if (event.button !== 0) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = this.track.scrollLeft;
      dragging = false;
    };

    const onPointerMove = (event) => {
      if (event.pointerType !== 'mouse') return;
      if (pointerId === null || pointerId !== event.pointerId) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!dragging) {
        if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) {
          return;
        }

        dragging = true;
        suppressClick = true;
        this.track.classList.add('is-dragging');

        try {
          this.track.setPointerCapture(event.pointerId);
        } catch (_) {}
      }

      event.preventDefault();
      this.track.scrollLeft = startScrollLeft - dx;
    };

    const onPointerEnd = (event) => {
      if (event.pointerType !== 'mouse') return;
      if (pointerId === null || event.pointerId !== pointerId) return;

      const wasDragging = dragging;
      reset();

      if (wasDragging) {
        requestAnimationFrame(snapToNearest);

        setTimeout(() => {
          suppressClick = false;
        }, 80);
      }
    };

    const onClickCapture = (event) => {
      if (!suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    this.track.addEventListener('pointerdown', onPointerDown);
    this.track.addEventListener('pointermove', onPointerMove);
    this.track.addEventListener('pointerup', onPointerEnd);
    this.track.addEventListener('pointercancel', onPointerEnd);
    this.track.addEventListener('lostpointercapture', onPointerEnd);
    this.track.addEventListener('click', onClickCapture, true);

    this.cleanupTrackDrag = () => {
      this.track?.removeEventListener('pointerdown', onPointerDown);
      this.track?.removeEventListener('pointermove', onPointerMove);
      this.track?.removeEventListener('pointerup', onPointerEnd);
      this.track?.removeEventListener('pointercancel', onPointerEnd);
      this.track?.removeEventListener('lostpointercapture', onPointerEnd);
      this.track?.removeEventListener('click', onClickCapture, true);
    };
  }

  /* ========================================================
     CURRENT PRODUCT VARIANT + DYNAMIC TOTAL
     ======================================================== */
  findMainProductForm() {
    const productId = this.dataset.currentProductId;
    if (!productId) return null;

    const forms = Array.from(
      document.querySelectorAll(
        `product-form-component[data-product-id="${productId}"]`
      )
    );

    return (
      forms.find((form) => !form.closest('ib-complete-routine')) ||
      null
    );
  }

  getCurrentVariantId() {
    const mainProductForm = this.findMainProductForm();

    const input = mainProductForm?.querySelector(
      'input[name="id"]'
    );

    return String(input?.value || '');
  }

  readPriceData() {
    if (!this.priceDataElement) return null;

    try {
      return JSON.parse(this.priceDataElement.textContent || '{}');
    } catch (_) {
      return null;
    }
  }

  setupVariantSync() {
    const mainProductForm = this.findMainProductForm();
    if (!mainProductForm) return;

    const update = () => {
      window.setTimeout(() => this.updateRoutineTotal(), 0);
    };

    mainProductForm.addEventListener('change', update, true);

    const observer = new MutationObserver(update);
    observer.observe(mainProductForm, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['value', 'checked', 'selected'],
    });

    this.cleanupVariantSync = () => {
      mainProductForm.removeEventListener('change', update, true);
      observer.disconnect();
    };
  }

  updateRoutineTotal() {
    const data = this.readPriceData();
    if (!data || !this.button) return;

    const currentItem = this.querySelector(
      '[data-ib-routine-item][data-current-product="true"]'
    );

    const currentVariantId = this.getCurrentVariantId();

    if (currentItem && currentVariantId) {
      currentItem.dataset.activeVariantId = currentVariantId;
    }

    let total = 0;

    const items = Array.from(
      this.querySelectorAll('[data-ib-routine-item]')
    );

    items.forEach((item) => {
      const isCurrent = item.dataset.currentProduct === 'true';

      if (isCurrent && currentVariantId && data.variants?.[currentVariantId]) {
        total += Number(data.variants[currentVariantId].price || 0);
      } else {
        total += Number(item.dataset.defaultPriceCents || 0);
      }
    });

    const content = this.button.querySelector(
      '.add-to-cart-text__content'
    );

    if (!content) return;

    const formatted = this.formatMoney(total);

    content.textContent = `${data.baseText || 'Aggiungi la Routine al Carrello'} · ${formatted}`;
  }

  formatMoney(cents) {
    const data = this.readPriceData();
    const variants = data?.variants || {};

    for (const variant of Object.values(variants)) {
      if (Number(variant.price) === Number(cents) && variant.formatted) {
        return variant.formatted;
      }
    }

    try {
      const currency =
        window.Shopify?.currency?.active ||
        document.documentElement.dataset.currency ||
        'EUR';

      return new Intl.NumberFormat(
        document.documentElement.lang || 'it-IT',
        {
          style: 'currency',
          currency,
        }
      ).format(Number(cents) / 100);
    } catch (_) {
      return `€${(Number(cents) / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  /* ========================================================
     CART ITEMS
     ======================================================== */
  getRoutineItems() {
    this.updateRoutineTotal();

    const currentVariantId = this.getCurrentVariantId();

    return Array.from(
      this.querySelectorAll('[data-ib-routine-item]')
    ).map((item) => {
      const isCurrent = item.dataset.currentProduct === 'true';

      const variantId = isCurrent
        ? currentVariantId || item.dataset.defaultVariantId
        : item.dataset.defaultVariantId;

      return {
        variantId: String(variantId || ''),
        quantity: 1,
      };
    });
  }

  getCartSectionIds() {
    const ids = new Set();

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.add(item.dataset.sectionId);
      }
    });

    return Array.from(ids);
  }

  async refreshCart() {
    const cartItemsComponent =
      document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');

      if (typeof cartItemsComponent.fetchCartData === 'function') {
        return cartItemsComponent.fetchCartData();
      }
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  /* ========================================================
     HORIZON NATIVE-LIKE ANIMATION
     ======================================================== */
  animateButton() {
    try {
      if (
        this.buttonComponent &&
        typeof this.buttonComponent.animateAddToCart === 'function'
      ) {
        this.buttonComponent.animateAddToCart();
      } else if (this.button) {
        this.button.dataset.added = 'true';

        window.setTimeout(() => {
          this.button?.removeAttribute('data-added');
        }, 800);
      }
    } catch (_) {}

    this.animateFlyToCart();
  }

  animateFlyToCart() {
    if (!this.button) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.routineImage;

    if (!cartIcon || !image || !customElements.get('fly-to-cart')) return;

    try {
      const fly = document.createElement('fly-to-cart');

      fly.classList.add('fly-to-cart--main');
      fly.style.setProperty('background-image', `url(${image})`);
      fly.style.setProperty('--start-opacity', '0');
      fly.source = this.button;
      fly.destination = cartIcon;

      document.body.appendChild(fly);
    } catch (_) {}
  }

  setLoading(value) {
    if (!this.button) return;

    this.isAdding = value;
    this.button.disabled = value;
    this.button.classList.toggle('is-loading', value);
    this.button.setAttribute('aria-busy', value ? 'true' : 'false');
  }

  announce(message) {
    if (!this.liveRegion) return;

    this.liveRegion.textContent = message;

    if (this.successTimer) {
      window.clearTimeout(this.successTimer);
    }

    this.successTimer = window.setTimeout(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = '';
      }
    }, 5000);
  }

  openCartDrawer() {
    const trigger = document.querySelector(
      '[data-testid="cart-drawer-trigger"]'
    );

    if (
      trigger instanceof HTMLElement &&
      trigger.getAttribute('aria-expanded') !== 'true'
    ) {
      trigger.click();
    }
  }

  /* ========================================================
     ADD THE 3 ITEMS — NO CUSTOM QUANTITY LIMIT
     ======================================================== */
  handleAddAll = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.button || this.isAdding) return;

    const items = this.getRoutineItems();

    if (
      items.length !== 3 ||
      items.some((item) => !item.variantId)
    ) {
      this.announce(
        'Uno dei prodotti della routine non è disponibile.'
      );
      return;
    }

    this.setLoading(true);
    this.animateButton();

    const sectionIds = this.getCartSectionIds();
    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: 1,
      })),
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        const message =
          data.description ||
          data.message ||
          'Non è stato possibile aggiungere la routine.';

        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: {
              description: data.description,
              errors: data.errors,
            },
          })
        );

        throw new Error(message);
      }

      const cart = await this.refreshCart();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ib-complete-routine',
          sourceId: this.id,
          itemCount: 3,
          productId: this.dataset.currentProductId,
          sections: data.sections,
          didError: false,
        },
      });

      this.announce('La routine è stata aggiunta al carrello.');

      window.setTimeout(() => {
        this.openCartDrawer();
      }, 320);
    } catch (error) {
      console.error('[IB Complete Routine]', error);

      try {
        deferredEventPromise.reject(error);
      } catch (_) {}

      this.button?.removeAttribute('data-added');

      this.announce(
        error?.message ||
        'Non è stato possibile aggiungere la routine.'
      );
    } finally {
      this.setLoading(false);

      window.setTimeout(() => {
        this.updateRoutineTotal();
      }, 900);
    }
  };
}

if (!customElements.get('ib-complete-routine')) {
  customElements.define(
    'ib-complete-routine',
    IBCompleteRoutine
  );
}